<template>
  <v-app style="background: #ffffff; min-height: auto">
    <v-container class="app-container">
      <v-row>
        <v-col cols="auto">
          <v-btn
            outlined
            color="primary"
            depressed
            @click="addCompanyHandler"
          >
            <v-icon left dark>mdi-plus-circle-outline</v-icon>
            {{ mess.button_add_company }}
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <toggle-buttons
            :buttons="toggleButtons"
            :active="viewType"
            v-model="viewType"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="4">
          <v-text-field
            v-model="search"
            append-icon="search"
            :label="mess.search_label"
            single-line
            hide-details
            outlined
            dense
            clearable
            class="elevation-0"
          />
        </v-col>

        <v-col class="d-flex">
          <customers-filter
            :data="items || []"
            :filter-fields="gridColumns"
            v-on:filtered="updateData"
          />
        </v-col>
      </v-row>

      <v-row v-show="viewType === 'list'" key="list">
        <v-col cols="12">
          <v-card class="pb-0 elevation-0">
            <customers-table
              :headers="headers"
              :items="filteredItems"
              :search="search"
              :select="true"
              class="customers-table"
              @open-company-info-dialog="openCompanyInfoDialog"
              @open-create-lead-dialog="openCreateLeadDialog"
              @change-company="changeCompany"
              @update-company="onUpdateCompany"
              @send-to-ns:company="onSendToNSCompany"
            />
          </v-card>
        </v-col>
      </v-row>

      <v-row v-show="viewType === 'map'" key="map" ref="map-container">
        <v-col cols="12">
          <v-sheet class="overflow-hidden" style="position: relative;">
            <customers-map
              :view-type="viewType"
              :filtered-items="filteredItems"
              :search-query="search"
              :map-container="$refs['map-container']"
              :is-toggle-buttons="isMapToggleButtons"
              @show-customer-card="customerCard = true"
              @change-company="changeCompany"
              @intersect-customers="data => (intersectItems = data)"
            />

            <customer-card
              :headers="headers"
              :is-visible="customerCard"
              v-model="customerCard"
              @show-company-info-dialog="item => openCompanyInfoDialog(item)"
              @open-create-lead-dialog="openCreateLeadDialog"
            />
          </v-sheet>

          <customers-table
            :headers="headers"
            :items="intersectItems"
            :select="true"
            class="customers-table"
            @show-customer-card="customerCard = true"
            @open-company-info-dialog="openCompanyInfoDialog"
            @open-create-lead-dialog="openCreateLeadDialog"
            @change-company="changeCompany"
            @update-company="onUpdateCompany"
            @send-to-ns:company="onSendToNSCompany"
          />
        </v-col>
      </v-row>

      <v-row>
        <company-info-dialog
          :is-visible.sync="companyInfoDialog"
          :company="companyInfoDialogBaseData"
          :detailed-company-data="detailedCompanyData"
          @open-create-lead-dialog="openCreateLeadDialog"
          @open-wishlist-adding-dialog="openWishlistAddingDialog"
          @open-request-adding-dialog="openRequestDialog"
          @close-dialog="
            Object.keys(detailedCompanyData).forEach(
              key => (detailedCompanyData[key] = null)
            )
          "
        />
      </v-row>

      <v-row>
        <add-company-dialog
          :company-data="addedToNsCompany"
          :is-emc="true"
          :is-visible.sync="addCompanyDialog"
          :sales-area-data="salesAreaData"
          :industry-data="industryData"
          :is-exist-company="isExistCompany"
          @add-company="openSnackBar"
          @update:company="updateCompanyHandler($event)"
        />
      </v-row>

      <v-row>
        <create-lead-dialog
          :is-visible.sync="createLeadDialog"
          :company-for-lead="companyForLead"
          :article-headers="articleHeaders"
          :market-area-filters="marketAreaFilters"
          @error="openSnackBar"
          @added-to-cart-single-item="openSnackBar"
        />
      </v-row>

      <v-row>
        <wishlist-adding-dialog
          :is-visible.sync="wishlistAddingDialog"
          :company="companyForWishlist"
          @wishlist-added="openSnackBar"
          @update-wishlists="updateWishlists"
        />
      </v-row>

      <v-row>
        <request-dialog-new
          :is-open="requestDialog"
          :company="companyForRequest"
          :themes="requestThemes"
          @close-dialog="requestDialog = false"
          @request-sent="openSnackBar"
          @update-requests="updateRequests"
        />
      </v-row>

      <v-row>
        <snackbar
          v-model="snackbar"
          :snackbar="snackbar"
          :snackbar-data="snackbarData"
        />
      </v-row>

      <widgets @open-snackbar="openSnackBar" />

      <to-top-button />

      <v-overlay :value="!appLoaded" absolute color="white" opacity="1">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        />
      </v-overlay>
    </v-container>
  </v-app>
</template>

<script>
// Libs
import axios from "axios";
import isEmpty from "lodash/isEmpty";

// Components
import CustomersMap from "@/components/CustomersMap";
import CustomersFilter from "@/components/filters/CustomersFilter";
import CustomersTable from "@/components/CustomersTable";
import CustomerCard from "@/components/CustomerCard";
import CompanyInfoDialog from "@/components/dialogs/CompanyInfoDialog";
import CreateLeadDialog from "@/components/dialogs/CreateLeadDialog";
import AddCompanyDialog from "@/components/dialogs/AddCompanyDialog";
import WishlistAddingDialog from "@/components/dialogs/WishlistAddingDialog";
import RequestDialogNew from "@/components/dialogs/RequestDialog.new";
import ToggleButtons from "@/components/ToggleButtons";
import ToTopButton from "@/components/ToTopButton";
import Snackbar from "@/components/Snackbar";
import Widgets from "@/components/Widgets";

// Services
import { messService } from "@/services/mess-service";

// API
import {
  getCustomers,
  getCompanyParamsLists,
  getMarketArea
} from "@/api/my-customers";

import createCompanyForLeadData from "@/models/createCompanyForLeadData";
import notifications from "@/models/notificationTemplates";

export default {
  name: "App",
  components: {
    RequestDialogNew,
    WishlistAddingDialog,
    CreateLeadDialog,
    AddCompanyDialog,
    ToggleButtons,
    CustomersTable,
    CustomersFilter,
    CustomersMap,
    CustomerCard,
    CompanyInfoDialog,
    ToTopButton,
    Snackbar,
    Widgets
  },
  data() {
    return {
      addedToNsCompany: null,
      appLoaded: false,
      viewType: "list",
      mapToggleButtons: false,
      snackbar: false,
      snackbarData: undefined,
      companyForLead: {},
      companyForWishlist: {},
      companyForRequest: {},
      wishlistAddingDialog: false,
      createLeadDialog: false,
      addCompanyDialog: false,
      companyInfoDialog: false,
      requestDialog: false,
      companyInfoDialogBaseData: {},
      customerCard: false,
      requestThemes: [],
      detailedCompanyData: {
        info: null,
        mve: null,
        ib: null,
        cp: null,
        orders: null,
        wishlists: null,
        requests: null
      },
      search: "",
      items: null,
      filteredItems: null,
      intersectItems: null,
      salesAreaData: [],
      marketAreaFilters: null,
      industryData: [],
      gridColumns: [
        { val: "process", label: "Process", filtrationsField: true },
        { val: "type", label: "Type", filtrationsField: true },
        { val: "subtype", label: "Subtype", filtrationsField: true },
        { val: "brand", label: "Brand", filtrationsField: true },
        { val: "model", label: "Model", filtrationsField: true }
      ],
      theme: window.theme,
      isExistCompany: false
    };
  },
  computed: {
    isMapToggleButtons() {
      if (isEmpty(this.items)) return false;

      const firstType = this.items[0].type;
      return this.items.some(elem => elem.type !== firstType);
    },
    toggleButtons() {
      return [
        {
          name: this.mess.button_show_on_map,
          type: "map",
          icon: "mdi-map-legend"
        },
        {
          name: this.mess.button_show_list,
          type: "list",
          icon: "mdi-format-list-bulleted"
        }
      ];
    },
    headers() {
      return [
        {
          text: this.mess["table_customers_header_name"],
          value: "name"
        },
        {
          text: "",
          value: "send_to_ns",
          sortable: false
        },
        {
          text: "",
          value: "create_deal",
          sortable: false
        },
        {
          text: this.mess["table_customers_header_ib"],
          value: "ib"
        },
        {
          text: this.mess["table_customers_header_cp"],
          value: "cp"
        },
        {
          text: this.mess["table_customers_header_mve"],
          value: "mve"
        },
        {
          text: this.mess["table_customers_header_cp_mve"],
          value: "cp_mve"
        },
        {
          text: this.mess["table_customers_header_deals"],
          value: "deals"
        },
        {
          text: this.mess["table_customers_header_wishlists"],
          value: "wishlists"
        },
        {
          text: this.mess["table_customers_header_requests"],
          value: "requests"
        }
      ];
    },
    articleHeaders() {
      const headers = [
        {
          text: this.mess["table_articles_header_code"],
          value: "code"
        },
        {
          text: this.mess["table_articles_header_oem"],
          value: "oem"
        },
        {
          text: this.mess["table_articles_header_name"],
          value: "name"
        },
        {
          text: "",
          value: "icons"
        },
        {
          text: this.mess["table_articles_header_available"],
          value: "available"
        },
        {
          text: this.mess["table_articles_header_shippingEstimate"],
          value: "shippingEstimate"
        },
        {
          text: this.mess["table_articles_header_price"],
          value: "price"
        },
        {
          text: this.mess["table_articles_header_quantity"],
          value: "qty",
          sortable: false
        },
        {
          text: "",
          value: "remove",
          sortable: false
        }
      ];
      if (this.priceType.isSecondPrice) {
        headers.splice(7, 0, {
          text: this.mess["table_articles_header_second_price"],
          value: "second_price"
        });
        return headers;
      }
      return headers;
    }
  },
  created() {
    getCustomers()
      .then(customers => {
        this.items = customers;
        this.filteredItems = customers;
        const active = customers.find(c => c.active);
        if (active) this.$store.dispatch("setSelectedCustomer", active);
      })
      .catch(() => {})
      .finally(() => (this.appLoaded = true));

    messService
      .getMess("personal.my_customers")
      .then(mess => this.$store.dispatch("setMess", mess))
      .then(() => {
        this.$vuetify.lang.locales.ru.dataIterator.loadingText = this.mess.table_loading;
        this.$vuetify.lang.locales.ru.noDataText = this.mess.table_no_data;
      })
      .catch(err => new Error(err));

    getCompanyParamsLists(["sales_area", "industry"]).then(
      ({ sales_area, industry }) => {
        this.salesAreaData = sales_area;
        this.industryData = industry;
      }
    );

    getMarketArea().then(({ market_area }) => {
      this.marketAreaFilters = market_area;
    });

    axios.get("/api/requestThemes").then(response => {
      if (response.data) {
        this.requestThemes = response.data;
      }
    });
  },
  methods: {
    addCompanyHandler() {
      this.addCompanyDialog = true;
      this.isExistCompany = false;
    },
    openSnackBar(data) {
      this.snackbar = true;
      this.snackbarData = data;
    },
    updateBalloonDot(customerID) {
      const balloonRows = Array.from(
        document.querySelectorAll("[data-customer-id]")
      );

      balloonRows.forEach(balloonRow => {
        balloonRow.classList.remove("active");

        if (balloonRow.dataset.customerId === customerID) {
          balloonRow.classList.add("active");
        }
      });
    },
    changeCompany({ id, name }) {
      this.updateBalloonDot(id);
      this.openSnackBar({
        status: "success",
        message: `${this.mess.notification_selected_company} ${name}`
      });
    },
    updateData(data) {
      this.filteredItems = data;
    },
    openCompanyInfoDialog({ id, name }) {
      this.companyInfoDialogBaseData = { id, name };
      this.companyInfoDialog = true;
      setTimeout(() => this.getCompanyInfoData(id), 200);
    },
    openWishlistAddingDialog(company) {
      this.wishlistAddingDialog = true;
      this.companyForWishlist = company;
    },
    openRequestDialog(company) {
      this.requestDialog = true;
      this.companyForRequest = company;
    },
    updateWishlists({ id }) {
      axios
        .get(`/api/company/wishlists?id=${id}`)
        .then(response => (this.detailedCompanyData.wishlists = response.data))
        .catch(err => new Error(err));
    },
    updateRequests({ id }) {
      axios
        .get(`/api/company/requests?id=${id}`)
        .then(response => (this.detailedCompanyData.requests = response.data))
        .catch(err => new Error(err));
    },
    openCreateLeadDialog(company) {
      this.createLeadDialog = true;
      const formData = new FormData();
      formData.append("company_id", company.id);
      const url =
        this.userType.sm ||
        this.userType.vimelco ||
        this.userType.partner ||
        this.userType.ersaProses ||
        this.userType.endUser
          ? "/local/ajax/crm/getCompanyEqList.php"
          : "/local/ajax/crm/getPSsWithEquipments.php";
      axios
        .post(url, formData)
        .then(response => {
          if (response.data.success) {
            this.companyForLead = createCompanyForLeadData({
              companyId: company.id,
              companyName: company.name,
              withoutPs:
                this.userType.sm ||
                this.userType.vimelco ||
                this.userType.partner ||
                this.userType.ersaProses ||
                this.userType.endUser,
              equipmentList: response.data.eq_list,
              productionSites: response.data.ps
            });
          } else {
            this.openSnackBar(notifications.error.getData);
          }
        })
        .catch(() => {
          this.createLeadDialog = false;
          this.openSnackBar(notifications.error.getData);
        });
    },
    getCompanyInfoData(id) {
      axios
        .get(`/api/company/info?id=${id}`)
        .then(response => (this.detailedCompanyData.info = response.data))
        .catch(err => new Error(err));

      axios
        .get(`/api/company/mve?id=${id}`)
        .then(response => (this.detailedCompanyData.mve = response.data))
        .catch(err => new Error(err));

      axios
        .get(`/api/company/ib?id=${id}`)
        .then(response => (this.detailedCompanyData.ib = response.data))
        .catch(err => new Error(err));

      axios
        .get(`/api/company/cp?id=${id}`)
        .then(response => (this.detailedCompanyData.cp = response.data))
        .catch(err => new Error(err));

      axios
        .get(`/api/company/orders?id=${id}`)
        .then(response => (this.detailedCompanyData.orders = response.data))
        .catch(err => new Error(err));

      axios
        .get(`/api/company/wishlists?id=${id}`)
        .then(response => (this.detailedCompanyData.wishlists = response.data))
        .catch(err => new Error(err));

      axios
        .get(`/api/company/requests?id=${id}`)
        .then(response => (this.detailedCompanyData.requests = response.data))
        .catch(err => new Error(err));
    },
    onUpdateCompany() {
      getCustomers()
        .then(customers => {
          this.items = customers;
          this.filteredItems = customers;
          const active = customers.find(c => c.active);
          if (active) this.$store.dispatch("setSelectedCustomer", active);
        })
        .catch(() => {})
        .finally(() => (this.appLoaded = true));
    },
    onSendToNSCompany(company) {
      this.addedToNsCompany = {
        ...company,
        company_id: company.id,
        category: window.initialData?.categoryPersonal,
        subsidiary: window.initialData?.subsidiaryPersonal
      };
      this.isExistCompany = true;
      this.addCompanyDialog = true;
    },
    updateCompanyHandler(data) {
      const targetCompany = this.filteredItems.find(item => item.id === data.company_id);
      targetCompany.isNS = true;
    }
  }
};
</script>

<style lang="scss">
@import "../../../styles/common-pages";
@import "styles/my_customers";
</style>
